import type { Amount } from '@orus.eu/amount'
import { amountToString } from '@orus.eu/amount'
import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { failure, isSuccess, type Result, success } from '@orus.eu/result'
import { type Language, type ParameterlessTranslationKey, translate } from '@orus.eu/translations'

import {
  AbstractDimension,
  type DimensionOptions,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export class StringsWithDataEnumDimension<
  NAME extends string,
  DATA extends { label: string } | { labelKey: ParameterlessTranslationKey } | { lci: Amount },
  ENTRIES extends ReadonlyArray<readonly [string, DATA]>,
> extends AbstractDimension<NAME, ENTRIES[number][0]> {
  readonly type = 'strings-with-data-enum'
  readonly entries: ENTRIES
  readonly values: readonly ENTRIES[number][0][]
  readonly data: { [key in ENTRIES[number][0]]: ENTRIES[number][1] }

  constructor(options: DimensionOptions<NAME, ENTRIES[number][0]> & { entries: ENTRIES }) {
    super(options)
    this.entries = options.entries
    this.data = Object.fromEntries(this.entries) as { [key in ENTRIES[number][0]]: DATA }
    const values = options.entries.map((entry) => entry[0])
    this.values = values

    // We need access to the dimension instance to access dimension members
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const dimension = this

    this.mcpAdapter = {
      parseValueFromMcp(mcpValueInput: string): Result<ENTRIES[number][0], string> {
        const validationResult = dimension.validateData(mcpValueInput)
        if (isSuccess(validationResult)) {
          return success(validationResult.output)
        }
        return failure(`Invalid input format: ${mcpValueInput}. ${this.formattingInstructions}`)
      },
      formattingInstructions: 'Use exactly one of the following values:\n- ' + this.values.join('\n- '),
    }
  }

  override formatToString(value: ENTRIES[number][0], opts: { language: Language }): string {
    const entry = this.data[value]

    if ('label' in entry && entry.label) {
      return entry.label
    }

    if ('labelKey' in entry && entry.labelKey) {
      return translate(entry.labelKey, opts.language)
    }

    if ('lci' in entry && entry.lci) {
      return amountToString(entry.lci, { displayDecimals: false, addCurrency: true })
    }

    return value
  }

  override get acceptedValues(): readonly ENTRIES[number][0][] {
    return this.values
  }

  override validateData(value: LooselyTypedValue): Result<ENTRIES[number][0], DimensionValidationProblem> {
    for (const entry of this.entries) {
      const validValue = entry[0]
      if (validValue === value) return success(validValue)
    }
    return dimensionValidationFailure(`Field ${this.name} has an invalid value`)
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<ENTRIES[number][0]> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(
        m.enumWithoutValueDescription(
          valueDocumentation,
          Object.fromEntries(this.values.map((value) => [value, null])) as { [key in ENTRIES[number][0]]: null },
        ),
      )
    },
    convertStateValueToTrackingValue: (stateValue: ENTRIES[number][0] | null | undefined): string | null => {
      if (!stateValue) return null
      return stateValue ?? null
    },
  }
}

export function getStringsWithDataEnumDimensionEntriesKey<
  NAME extends string,
  DATA extends { label: string } | { labelKey: ParameterlessTranslationKey },
  ENTRIES extends ReadonlyArray<readonly [string, DATA]>,
>(stringsWithDataEnumDimension: StringsWithDataEnumDimension<NAME, DATA, ENTRIES>): ReadonlyArray<string> {
  return stringsWithDataEnumDimension.entries.map((entry) => entry[0])
}
